import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../common/Button';
import Input from '../common/Input';
import Card from '../common/Card';
import toast from 'react-hot-toast';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
}

const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'login',
}) => {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const { login, signup } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('로그인되었습니다!');
      } else {
        if (formData.password !== formData.confirmPassword) {
          toast.error('비밀번호가 일치하지 않습니다.');
          return;
        }
        await signup(formData.username, formData.email, formData.password);
        toast.success('회원가입이 완료되었습니다!');
      }
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* 배경 오버레이 */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* 모달 컨텐츠 */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* 헤더 */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'login' ? '로그인' : '회원가입'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* 폼 */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {mode === 'signup' && (
              <Input
                label="사용자명"
                placeholder="사용자명을 입력하세요"
                value={formData.username}
                onChange={(value) => handleInputChange('username', value)}
                disabled={loading}
                fullWidth
              />
            )}

            <Input
              label="이메일"
              type="email"
              placeholder="이메일을 입력하세요"
              value={formData.email}
              onChange={(value) => handleInputChange('email', value)}
              disabled={loading}
              fullWidth
            />

            <Input
              label="비밀번호"
              type="password"
              placeholder="비밀번호를 입력하세요"
              value={formData.password}
              onChange={(value) => handleInputChange('password', value)}
              disabled={loading}
              fullWidth
            />

            {mode === 'signup' && (
              <Input
                label="비밀번호 확인"
                type="password"
                placeholder="비밀번호를 다시 입력하세요"
                value={formData.confirmPassword}
                onChange={(value) => handleInputChange('confirmPassword', value)}
                disabled={loading}
                fullWidth
              />
            )}

            <Button
              type="submit"
              variant="primary"
              loading={loading}
              fullWidth
              className="mt-6"
            >
              {mode === 'login' ? '로그인' : '회원가입'}
            </Button>

            <div className="text-center mt-4">
              <button
                type="button"
                onClick={switchMode}
                className="text-sm text-primary-600 hover:text-primary-500"
                disabled={loading}
              >
                {mode === 'login' 
                  ? '계정이 없으신가요? 회원가입' 
                  : '이미 계정이 있으신가요? 로그인'
                }
              </button>
            </div>
          </form>

          {/* 소셜 로그인 (목업) */}
          <div className="px-6 pb-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">또는</span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <Button
                variant="outline"
                fullWidth
                disabled={loading}
              >
                Google로 계속하기
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;